import { useContext, useEffect, useState } from 'react';
import { getLaminasApiData } from 'services/api';
import { useTranslation } from 'react-i18next';

import Header from 'components/header';
import {
  Category, CategoryReponse, DealItem, DealItemReponse, GroupedDeal,
} from 'models/partner';
import { MarqueeItem } from 'components/marquee';
import Skeleton from 'components/skeleton';
import { StoreContext } from 'models/import';
import { ProStatusLevel } from 'models/general';
import { randomShuffle, sortByPos } from 'services/util';
import AnchorTabs, { AnchorTabType } from 'components/anchorTabs';
import DealCards from 'components/dealCards';
import { DealModifier } from 'components/dealCard';
import Banner from 'components/banner';
import PartnerHeader from './partnerHeader';

function PartnersComponent() {
  const { userSettings, isProPage } = useContext(StoreContext);
  const [featuredDeals, setFeaturedDeals] = useState([] as DealItem[]);
  const [featuredImages, setFeaturedImages] = useState([] as MarqueeItem[]);
  const [groupedDeals, setGroupedDeals] = useState([] as GroupedDeal[]);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const getCategories = () => getLaminasApiData<CategoryReponse>('/benefits/deal-category/list');
  const getDeals = () => getLaminasApiData<DealItemReponse>('/benefits/deal/list');

  // eslint-disable-next-line @typescript-eslint/comma-dangle
  const getSortedItems = <T extends { position: number | null }>(arr: T[]): T[] => {
    const orderedItems = arr.filter((item: T) => item.position !== null);
    const unorderedItems = arr.filter((item: T) => item.position === null);

    return [...sortByPos(orderedItems), ...randomShuffle(unorderedItems)];
  };

  const getFeaturedDeals = (deals: DealItem[]): DealItem[] => {
    const topPartners = deals.filter((deal) => deal.partner?.top_partner);

    return getSortedItems(topPartners);
  };

  const getGroupedDeals = (
    _deals: DealItem[],
    _categories: Category[],
  ): GroupedDeal[] => _categories.map((cat: Category) => {
    const items = _deals.filter((d: DealItem) => d.category?.name === cat.name);

    const topPartners = items.filter((d: DealItem) => d.partner?.top_partner);
    const regularPartners = items.filter((d: DealItem) => !d.partner?.top_partner);

    const sortedTopPartners = getSortedItems(topPartners);
    const sortedRegularPartners = getSortedItems(regularPartners);
    const sortedItems: DealItem[] = [...sortedTopPartners, ...sortedRegularPartners];

    return { label: cat.display_name, items: sortedItems };
  });

  useEffect(() => {
    Promise.all([getCategories(), getDeals()])
      .then((res: [CategoryReponse, DealItemReponse]) => {
        const [categoriesRes, dealsRes] = res;
        const categories = categoriesRes.data;
        const deals = dealsRes.data;

        if (categories && deals) {
          const sortedDeals = deals.map((deal) => (
            {
              ...deal,
              tags: getSortedItems(deal.tags),
            }
          ));

          const sortedCategories = getSortedItems(categories);
          const filteredGroups = getGroupedDeals(sortedDeals, sortedCategories)
            .filter((group) => group.items.length);

          const featuredDealItems = getFeaturedDeals(sortedDeals);
          const featuredImageItems = deals.map((item) => ({
            url: item.partner?.logo_uri || '',
            alt: item.partner?.display_name || '',
          }));

          setFeaturedDeals(featuredDealItems);
          setFeaturedImages(featuredImageItems);
          setGroupedDeals(filteredGroups);
          setIsLoading(false);
        }
      }, () => {
        setIsLoading(true);
      });
  }, []);

  return (
    <>
      {!isProPage && (
        <>
          <div className="ct_section _padded">
            <div className="ct_section-inner _centered">
              <PartnerHeader marqueeItems={featuredImages} />
            </div>
          </div>
          <div className="ct_section _secondary _padded">
            <div className="ct_section-inner _centered">
              <Header
                title={t('Top Partners', { ns: 'partner' })}
                headline={t('CoinTracking Featured Deals', { ns: 'partner' })}
              />
              <DealCards
                items={featuredDeals}
                modifier={DealModifier.FEATURED}
                skeletons={[...Array.from(Array(8)).map((e, i) => <Skeleton key={i} height="470px" />)]}
                isLoading={isLoading}
              />
            </div>
          </div>
          <div className="ct_section _padded">
            <div className="ct_section-inner _centered">
              <Header
                title={t('All Partners', { ns: 'partner' })}
                headline={t('All deals, offers and benefits', { ns: 'partner' })}
              />
              {groupedDeals && !!groupedDeals.length && (
              <AnchorTabs groups={groupedDeals} showHeader type={AnchorTabType.PARTNER} />
              )}
            </div>
          </div>
          {userSettings.proStatus === ProStatusLevel.FREE && (
          <div className="ct_section _padded">
            <div className="ct_section-inner _centered">
              <Banner
                title={t('Premium Account', { ns: 'partner' })}
                headline={t('Do you want more exclusive benefits?', { ns: 'partner' })}
                subline={t('Upgrade your account to Premium', { ns: 'partner' })}
                btnText={t('Upgrade Now', { ns: 'partner' })}
              />
            </div>
          </div>
          )}
        </>
      )}
      {isProPage && (
      <div className="ct_section _secondary">
        <div className="ct_section-inner _centered">
          {groupedDeals && !!groupedDeals.length && (
          <AnchorTabs groups={groupedDeals} showHeader type={AnchorTabType.PARTNER} />
          )}
        </div>
      </div>
      )}
    </>
  );
}

PartnersComponent.defaultProps = { ...{} };

export default PartnersComponent;
